import React, { useState } from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Briefcase, Search, Check } from 'lucide-react';

interface Department {
  id: string;
  name: string;
}

export interface ProfessionalInfoFormData {
  position?: string;
  department_id?: string;
  hire_date?: string;
}

interface ProfessionalInfoSectionProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<ProfessionalInfoFormData>;
  departments: Department[];
  disabled?: boolean;
  selectedDepartmentIds?: string[];
  onDepartmentChange?: (ids: string[]) => void;
  darkMode?: boolean;
}

export const ProfessionalInfoSection: React.FC<ProfessionalInfoSectionProps> = ({
  register,
  errors,
  departments,
  disabled = false,
  selectedDepartmentIds = [],
  onDepartmentChange,
  darkMode = false,
}) => {
  const [query, setQuery] = useState('');

  const toggleDepartment = (id: string) => {
    if (!onDepartmentChange) return;
    if (selectedDepartmentIds.includes(id)) {
      onDepartmentChange(selectedDepartmentIds.filter(d => d !== id));
    } else {
      onDepartmentChange([...selectedDepartmentIds, id]);
    }
  };

  const filtered = departments.filter(d =>
    d.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className={`flex items-center gap-2 pb-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <Briefcase className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Informações Profissionais</h3>
      </div>

      {/* Form Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Position */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Cargo
          </label>
          <input
            type="text"
            {...register('position')}
            disabled={disabled}
            className={`w-full py-2 px-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 ${
              darkMode
                ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
                : 'border-gray-200 disabled:bg-gray-100'
            }`}
            placeholder="Ex: Engenheiro"
          />
        </div>

        {/* Hire Date */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Data de Admissão
          </label>
          <input
            type="date"
            {...register('hire_date')}
            disabled={disabled}
            className={`w-full py-2 px-3 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 ${
              darkMode
                ? 'bg-gray-800 border-gray-700 text-white'
                : 'border-gray-200 disabled:bg-gray-100'
            }`}
          />
        </div>

        {/* Department — full width, searchable multi-select */}
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-1.5">
            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Departamentos
            </label>
            {selectedDepartmentIds.length > 0 && (
              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                {selectedDepartmentIds.length} {selectedDepartmentIds.length === 1 ? 'selecionado' : 'selecionados'}
              </span>
            )}
          </div>

          {departments.length === 0 ? (
            <p className={`text-xs italic py-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhum departamento cadastrado</p>
          ) : (
            <div className={`rounded-lg border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {/* Search bar */}
              <div className={`flex items-center gap-2 px-3 py-2 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
                <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                  placeholder="Buscar departamento..."
                  disabled={disabled}
                  className={`flex-1 text-xs bg-transparent outline-none placeholder:text-gray-400 disabled:opacity-50 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className={`text-sm leading-none ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* List */}
              <div className={`overflow-y-auto max-h-44 divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {filtered.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-center text-gray-400">
                    Nenhum resultado para "{query}"
                  </p>
                ) : (
                  filtered.map(dept => {
                    const selected = selectedDepartmentIds.includes(dept.id);
                    return (
                      <button
                        key={dept.id}
                        type="button"
                        onClick={() => toggleDepartment(dept.id)}
                        disabled={disabled}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors disabled:pointer-events-none disabled:opacity-50 ${
                          selected
                            ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50/60'
                            : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                          selected
                            ? 'bg-blue-600 border-blue-600'
                            : darkMode ? 'border-gray-600' : 'border-gray-300'
                        }`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className={`text-xs flex-1 ${
                          selected
                            ? darkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'
                            : darkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {dept.name}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalInfoSection;
